<script setup>
import { ref, computed, onMounted } from 'vue'
import { $router } from '@/router'
import { dialog } from '@/common/dialog'
import { hcu } from '@/common/hcu'
import { useTitle } from '@/composables/title'
import { useTranslate } from '@/composables/language'
import { useMenu, useSubmenu, useTopBar } from '@/composables/menu'
import { useSessionTimer } from '@/composables/session'
import { ConfigService } from '@/services/ConfigService.js'
import { SetupService } from '@/services/SetupService.js'
import DebugFooter from '@/components/Maintenance/DebugFooter.vue'
import Spinner from '@/components/Spinner.vue'
import Button from '@/components/Button.vue'

const { t } = useTranslate();
const configService = new ConfigService();
const setupService = new SetupService();

/******************************************************************************************
 * Wizard state
 ******************************************************************************************/
const steps = ['welcome', 'rotation', 'setup_code', 'health'];
const currentStep = ref(0);
const stepKey = computed(() => steps[currentStep.value]);

/******************************************************************************************
 * Screen rotation
 ******************************************************************************************/
const rotation = ref("0");
const rotationOptions = [
    { value: "0", label: "rotation_0" },
    { value: "90", label: "rotation_90" },
    { value: "180", label: "rotation_180" },
    { value: "270", label: "rotation_270" },
];

function applyRotation(value) {
    rotation.value = value;
    //Apply CSS rotation for preview
    document.documentElement.style.transform = value === "0" ? "" : `rotate(${value}deg)`;
}

/******************************************************************************************
 * Setup code entry
 ******************************************************************************************/
const code1 = ref("");
const code2 = ref("");
const code3 = ref("");
const code4 = ref("");
const setupStatus = ref("input"); //input, syncing, success, error
const setupError = ref(null);

async function submitSetupCode() {
    try {
        setupStatus.value = "syncing";
        setupError.value = null;

        let code = `${code1.value}-${code2.value}-${code3.value}-${code4.value}`;
        let result = await setupService.start({ code });

        if (result.error) {
            throw new Error(result.error);
        }

        setupStatus.value = "success";
    }
    catch (err) {
        setupStatus.value = "error";
        setupError.value = err.message;
    }
}

function nextCodeInput(e) {
    let id = e.target.id;
    let num = parseInt(id.replace("wcode", ""));

    let code = null;
    switch (num) {
        case 1:
            code = code1;
            break;
        case 2:
            code = code2;
            break;
        case 3:
            code = code3;
            break;
        case 4:
            code = code4;
            break;
    }

    if (e.keyCode == 8 && code.value == "" && num > 1)
        document.getElementById("wcode" + (num - 1)).focus();
    else if (e.keyCode == 9) {
        //Tab - default behavior
    }
    else if (e.keyCode == 13) {
        submitSetupCode();
        e.preventDefault();
        e.stopPropagation();
    }
    else if ((e.keyCode >= 65 && e.keyCode <= 90) || (e.keyCode >= 48 && e.keyCode <= 57)) {
        if (code.value.length < 4)
            e.target.value += ("" + e.key).toUpperCase();
        else if (num < 4)
            document.getElementById("wcode" + (num + 1)).focus();
        e.preventDefault();
        e.stopPropagation();
    }
    else {
        e.preventDefault();
        e.stopPropagation();
    }

    code.value = e.target.value;
}

/******************************************************************************************
 * Health check
 ******************************************************************************************/
const healthChecks = ref([]);
const healthRunning = ref(false);
const healthDone = ref(false);

const allHealthPassed = computed(() => healthChecks.value.every(c => c.status === "ok" || c.status === "skip"));

async function runHealthChecks() {
    healthRunning.value = true;
    healthDone.value = false;

    healthChecks.value = [
        { id: "internet", status: "checking" },
        { id: "database", status: "pending" },
        { id: "cabinet", status: "pending" },
    ];

    //Check internet
    try {
        healthChecks.value[0].status = "checking";
        let result = await configService.getStatus({ check: true });
        healthChecks.value[0].status = result.online ? "ok" : "fail";
    }
    catch (err) {
        healthChecks.value[0].status = "fail";
    }

    //Check database
    try {
        healthChecks.value[1].status = "checking";
        let result = await configService.getSettings();
        healthChecks.value[1].status = result ? "ok" : "fail";
    }
    catch (err) {
        healthChecks.value[1].status = "fail";
    }

    //Check cabinet controllers
    try {
        healthChecks.value[2].status = "checking";
        let result = await hcu.cabinet().getInstances2();
        if (!result.controllers || result.controllers.length === 0) {
            healthChecks.value[2].status = "skip";
        }
        else {
            let anyResponding = result.controllers.some(c => c.state !== "error");
            healthChecks.value[2].status = anyResponding ? "ok" : "fail";
        }
    }
    catch (err) {
        healthChecks.value[2].status = "fail";
    }

    healthRunning.value = false;
    healthDone.value = true;
}

/******************************************************************************************
 * Navigation
 ******************************************************************************************/
function nextStep() {
    if (currentStep.value < steps.length - 1) {
        currentStep.value++;

        //Auto-start health checks when entering the health step
        if (stepKey.value === "health")
            runHealthChecks();
    }
}

function prevStep() {
    if (currentStep.value > 0)
        currentStep.value--;
}

async function finishWizard() {
    try {
        //Set flag so wizard is not shown again
        await configService.setFlag("wizard-completed");

        //Reset rotation style (the actual rotation is persisted in config)
        document.documentElement.style.transform = "";

        //Navigate to the main application
        $router.push({ name: "Index" });
    }
    catch (err) {
        dialog(err);
    }
}

/******************************************************************************************
 * Lifecycle
 ******************************************************************************************/
onMounted(() => {
    useTitle(t('wizard.title'));
    useMenu(false);
    useSubmenu(false);
    useTopBar(false);
    useSessionTimer(false);
});

/******************************************************************************************
 * Health check helper
 ******************************************************************************************/
function healthIcon(status) {
    switch (status) {
        case "ok": return "fa-circle-check text-green-500";
        case "fail": return "fa-circle-xmark text-red-500";
        case "checking": return "fa-spinner fa-spin text-blue-500";
        case "skip": return "fa-circle-minus text-gray-400";
        default: return "fa-circle text-gray-300";
    }
}

function healthLabel(check) {
    let prefix = `wizard.health_${check.id}`;
    switch (check.status) {
        case "ok": return t(`${prefix}_ok`);
        case "fail": return t(`${prefix}_fail`);
        case "checking": return t(`${prefix}_checking`);
        case "skip":
            if (check.id === "cabinet") return t('wizard.health_cabinet_none');
            return t(`${prefix}_checking`);
        default: return t(`wizard.health_${check.id}`);
    }
}
</script>

<template>
    <div class="min-h-screen bg-gray-50 dark:bg-gray-900 flex flex-col">

        <!-- Step indicator -->
        <div class="w-full max-w-2xl mx-auto pt-8 px-4">
            <div class="flex items-center justify-center space-x-2">
                <template v-for="(step, i) in steps" :key="step">
                    <div class="flex items-center">
                        <div class="w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-colors"
                            :class="{
                                'bg-blue-600 text-white': i === currentStep,
                                'bg-green-500 text-white': i < currentStep,
                                'bg-gray-200 text-gray-500 dark:bg-gray-700 dark:text-gray-400': i > currentStep,
                            }">
                            <i class="fa fa-check" v-if="i < currentStep"></i>
                            <span v-else>{{ i + 1 }}</span>
                        </div>
                        <span class="ml-2 text-sm hidden sm:inline" :class="{
                            'text-blue-600 font-medium dark:text-blue-400': i === currentStep,
                            'text-green-600 dark:text-green-400': i < currentStep,
                            'text-gray-400 dark:text-gray-500': i > currentStep,
                        }">
                            {{ $t(`wizard.step_${step}`) }}
                        </span>
                    </div>
                    <div class="w-12 h-0.5 bg-gray-300 dark:bg-gray-600" v-if="i < steps.length - 1"
                        :class="{ 'bg-green-500 dark:bg-green-500': i < currentStep }"></div>
                </template>
            </div>
        </div>

        <!-- Content area -->
        <div class="flex-1 flex items-center justify-center px-4 py-8">
            <div class="w-full max-w-xl">

                <!-- Step 1: Welcome -->
                <div v-if="stepKey === 'welcome'" class="text-center">
                    <div class="mb-6">
                        <i class="fa fa-wand-magic-sparkles text-blue-500" style="font-size: 64px;"></i>
                    </div>
                    <h1 class="text-3xl font-bold mb-4 text-gray-900 dark:text-white">{{ $t('wizard.welcome_title') }}
                    </h1>
                    <p class="text-lg text-gray-600 dark:text-gray-300 mb-8">{{ $t('wizard.welcome_text') }}</p>
                </div>

                <!-- Step 2: Screen rotation -->
                <div v-if="stepKey === 'rotation'" class="text-center">
                    <div class="mb-6">
                        <i class="fa fa-display text-blue-500" style="font-size: 48px;"></i>
                    </div>
                    <h1 class="text-2xl font-bold mb-2 text-gray-900 dark:text-white">{{ $t('wizard.rotation_title') }}
                    </h1>
                    <p class="text-gray-600 dark:text-gray-300 mb-6">{{ $t('wizard.rotation_text') }}</p>

                    <div class="grid grid-cols-2 gap-3 max-w-sm mx-auto">
                        <button v-for="opt in rotationOptions" :key="opt.value" @click="applyRotation(opt.value)"
                            class="px-4 py-3 rounded-lg border-2 text-sm font-medium transition-colors" :class="{
                                'border-blue-500 bg-blue-50 text-blue-700 dark:bg-blue-900 dark:text-blue-300': rotation === opt.value,
                                'border-gray-200 bg-white text-gray-700 hover:border-gray-300 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-300': rotation !== opt.value,
                            }">
                            {{ $t(`wizard.${opt.label}`) }}
                        </button>
                    </div>

                    <p class="text-sm text-gray-500 dark:text-gray-400 mt-6">{{ $t('wizard.rotation_confirm') }}</p>
                </div>

                <!-- Step 3: Setup code -->
                <div v-if="stepKey === 'setup_code'" class="text-center">
                    <div class="mb-6">
                        <i class="fa fa-key text-blue-500" style="font-size: 48px;"></i>
                    </div>
                    <h1 class="text-2xl font-bold mb-2 text-gray-900 dark:text-white">{{ $t('wizard.setup_code_title')
                    }}</h1>
                    <p class="text-gray-600 dark:text-gray-300 mb-6">{{ $t('wizard.setup_code_text') }}</p>

                    <!-- Code input -->
                    <div class="mb-4" v-if="setupStatus === 'input' || setupStatus === 'error'">
                        <div class="flex items-center justify-center gap-2">
                            <input type="text" id="wcode1" class="code-input" maxlength="4" @keydown="nextCodeInput">
                            <span class="text-gray-400 text-xl">-</span>
                            <input type="text" id="wcode2" class="code-input" maxlength="4" @keydown="nextCodeInput">
                            <span class="text-gray-400 text-xl">-</span>
                            <input type="text" id="wcode3" class="code-input" maxlength="4" @keydown="nextCodeInput">
                            <span class="text-gray-400 text-xl">-</span>
                            <input type="text" id="wcode4" class="code-input" maxlength="4" @keydown="nextCodeInput">
                        </div>
                        <div class="text-red-500 text-sm mt-3" v-if="setupStatus === 'error'">{{ setupError }}</div>
                        <Button type="primary" class="mt-4" @click="submitSetupCode()">{{ $t('setup.submit') }}</Button>
                    </div>

                    <!-- Syncing -->
                    <div v-if="setupStatus === 'syncing'" class="py-8">
                        <Spinner size="10" style="margin: 0 auto;" />
                        <p class="text-gray-500 mt-4">{{ $t('wizard.setup_code_syncing') }}</p>
                    </div>

                    <!-- Success -->
                    <div v-if="setupStatus === 'success'" class="py-8">
                        <i class="fa fa-circle-check text-green-500" style="font-size: 48px;"></i>
                        <p class="text-green-600 font-medium mt-4">{{ $t('wizard.setup_code_success') }}</p>
                    </div>

                    <p class="text-sm text-gray-400 dark:text-gray-500 mt-6">{{ $t('wizard.setup_code_skip_text') }}</p>
                </div>

                <!-- Step 4: Health check -->
                <div v-if="stepKey === 'health'" class="text-center">
                    <div class="mb-6">
                        <i class="fa fa-stethoscope text-blue-500" style="font-size: 48px;"></i>
                    </div>
                    <h1 class="text-2xl font-bold mb-2 text-gray-900 dark:text-white">{{ $t('wizard.health_title') }}
                    </h1>
                    <p class="text-gray-600 dark:text-gray-300 mb-6" v-if="!healthDone">{{ $t('wizard.health_text') }}
                    </p>

                    <!-- Check list -->
                    <div class="max-w-sm mx-auto text-left space-y-4">
                        <div v-for="check in healthChecks" :key="check.id"
                            class="flex items-center gap-3 px-4 py-3 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
                            <i class="fa" :class="healthIcon(check.status)" style="font-size: 20px;"></i>
                            <span class="text-sm text-gray-700 dark:text-gray-300">{{ healthLabel(check) }}</span>
                        </div>
                    </div>

                    <!-- Result summary -->
                    <div class="mt-6" v-if="healthDone">
                        <p class="text-green-600 font-medium" v-if="allHealthPassed">
                            <i class="fa fa-circle-check mr-2"></i>{{ $t('wizard.health_all_ok') }}
                        </p>
                        <p class="text-amber-600 text-sm" v-else>
                            <i class="fa fa-triangle-exclamation mr-2"></i>{{ $t('wizard.health_some_failed') }}
                        </p>
                        <Button type="secondary" class="mt-4" @click="runHealthChecks()"
                            v-if="!allHealthPassed && !healthRunning">
                            <i class="fa fa-rotate-right mr-2"></i>{{ $t('wizard.health_retry') }}
                        </Button>
                    </div>
                </div>

            </div>
        </div>

        <!-- Navigation buttons -->
        <div class="w-full max-w-xl mx-auto px-4 pb-8">
            <div class="flex justify-between">
                <Button type="secondary" @click="prevStep()" v-if="currentStep > 0">
                    <i class="fa fa-arrow-left mr-2"></i>{{ $t('wizard.previous') }}
                </Button>
                <div v-else></div>

                <div class="flex gap-2">
                    <!-- Skip button for setup code step -->
                    <Button type="secondary" @click="nextStep()"
                        v-if="stepKey === 'setup_code' && setupStatus !== 'syncing'">
                        {{ $t('wizard.skip') }}
                    </Button>

                    <!-- Next button -->
                    <Button type="primary" @click="nextStep()"
                        v-if="currentStep < steps.length - 1 && stepKey !== 'setup_code'">
                        {{ $t('wizard.next') }}<i class="fa fa-arrow-right ml-2"></i>
                    </Button>

                    <!-- Next after successful setup code sync -->
                    <Button type="primary" @click="nextStep()"
                        v-if="stepKey === 'setup_code' && setupStatus === 'success'">
                        {{ $t('wizard.next') }}<i class="fa fa-arrow-right ml-2"></i>
                    </Button>

                    <!-- Finish button on last step -->
                    <Button type="primary" @click="finishWizard()"
                        v-if="currentStep === steps.length - 1 && healthDone">
                        {{ $t('wizard.finish') }}<i class="fa fa-check ml-2"></i>
                    </Button>
                </div>
            </div>
        </div>

        <DebugFooter />
    </div>
</template>

<style scoped>
.code-input {
    @apply bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 w-20 p-2.5 text-center uppercase dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500;
}
</style>
